import React, { useEffect, useRef } from 'react';
import { Database, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  referenceKey,
  type CollectionReference,
} from '@/utils/collection-references';

export interface CollectionMentionProps {
  /** Filtered and capped by the caller, which also owns the highlight. */
  matches: CollectionReference[];
  activeIndex: number;
  selectedKeys: Set<string>;
  onSelect: (reference: CollectionReference) => void;
  onHover: (index: number) => void;
  isLoading?: boolean;
  className?: string;
}

// Not cmdk: it only handles keys on its own input, and here the caret stays in
// the textarea, so the caller drives navigation.
export const CollectionMention: React.FC<CollectionMentionProps> = ({
  matches,
  activeIndex,
  selectedKeys,
  onSelect,
  onHover,
  isLoading = false,
  className,
}) => {
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <div
      className={cn(
        'absolute bottom-full left-0 z-50 mb-2 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-md',
        className,
      )}
    >
      <ul
        role='listbox'
        aria-label='Data Collections'
        className='max-h-64 overflow-y-auto py-1'
      >
        {isLoading && (
          <li className='px-3 py-2 text-sm text-muted-foreground'>
            Loading data collections...
          </li>
        )}
        {!isLoading && matches.length === 0 && (
          <li className='px-3 py-2 text-sm text-muted-foreground'>
            No data collections match.
          </li>
        )}
        {!isLoading &&
          matches.map((reference, index) => {
            const key = referenceKey(reference);
            const isSelected = selectedKeys.has(key);
            const isActive = index === activeIndex;
            const Icon = reference.kind === 'dataset' ? Database : FileText;
            return (
              <li key={key} role='none'>
                <button
                  ref={isActive ? activeRef : undefined}
                  type='button'
                  role='option'
                  aria-selected={isActive}
                  // Not the DOM `disabled` attr: that swallows the click, so an
                  // added row couldn't dismiss the list the way keyboard does.
                  aria-disabled={isSelected}
                  // Keep focus in the textarea so the caret never moves.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => onHover(index)}
                  onClick={() => onSelect(reference)}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm',
                    isActive && 'bg-accent',
                    isSelected && 'opacity-50',
                  )}
                >
                  <Icon className='size-4 shrink-0 text-muted-foreground' />
                  <span className='truncate'>{reference.name}</span>
                  {reference.kind === 'dataset' && (
                    <span className='truncate text-xs text-muted-foreground'>
                      {reference.collectionName}
                    </span>
                  )}
                  {isSelected && (
                    <span className='ml-auto shrink-0 text-xs text-muted-foreground'>
                      added
                    </span>
                  )}
                </button>
              </li>
            );
          })}
      </ul>
    </div>
  );
};

export default CollectionMention;
